// Sidebar — operator nav for the Bvndle Command Centre.
//
// /supply + /demand are the two strategy work surfaces — KPI question on the
// left, row-level commitments on the right, hypothesis tethered to delivery
// on one page. Meeting Log captures session notes.
//
// /knowledge is the unified intelligence surface (Ask · Live data ·
// Documents). Live data lives there too now — funnel + Metabase + future
// cross-source signals all read through the same lens.
//
// Audit is the diagnostic surface. SYM + the five-field philosophy live on
// /about.

import { NavLink } from "react-router-dom";

import { useAuth } from "./AuthGate";

interface NavSpec {
  to: string;
  label: string;
  icon: string;
}

const COMMAND: NavSpec[] = [
  { to: "/supply", label: "Supply · Issuance", icon: "↑" },
  { to: "/demand", label: "Demand · Redemption", icon: "↓" },
  { to: "/meeting-log", label: "Meeting Log", icon: "✎" },
];

const DIAGNOSTICS: NavSpec[] = [
  { to: "/team-capacity", label: "Team capacity", icon: "◉" },
  { to: "/audit", label: "Audit & Playback", icon: "⟲" },
];

const REPORTS: NavSpec[] = [
  { to: "/report/one-pager", label: "Redemption 1-pager", icon: "▤" },
  { to: "/report/chairman", label: "Chairman briefing", icon: "❑" },
];

// One KNOWLEDGE entry — the IntelligencePage holds the three frames
// (Ask · Live data · Documents) behind a tab strip. Single mental model:
// "where do I go to read?" → Knowledge. "where do I go to commit?" →
// Supply / Demand.
const KNOWLEDGE: NavSpec[] = [
  { to: "/knowledge", label: "Knowledge", icon: "❖" },
];

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? "sidebar__item is-active" : "sidebar__item";
}

export function Sidebar() {
  const { session, signOut } = useAuth();
  // Group order signals what the team is meant to check first thing in the
  // morning: KNOWLEDGE (the intelligence layer — read here first; tensions
  // surface here; ask cross-corpus questions) → COMMAND (open S/D, work
  // your commitments) → REPORTS (Chairman 1-pager when Nonso asks) →
  // DIAGNOSTICS (audit / playback) → SETTINGS (rarely touched). Settings
  // is no longer pinned to the bottom — it flows in line so the eye reads
  // top-to-bottom without skipping.
  return (
    <aside className="sidebar">
      <div className="sidebar__group">KNOWLEDGE</div>
      {KNOWLEDGE.map((n) => (
        <NavLink key={n.to} to={n.to} className={navClass}>
          <span className="sidebar__icon">{n.icon}</span>
          {n.label}
        </NavLink>
      ))}

      <div className="sidebar__group">COMMAND</div>
      {COMMAND.map((n) => (
        <NavLink key={n.to} to={n.to} className={navClass}>
          <span className="sidebar__icon">{n.icon}</span>
          {n.label}
        </NavLink>
      ))}

      <div className="sidebar__group">REPORTS</div>
      {REPORTS.map((n) => (
        <NavLink key={n.to} to={n.to} className={navClass}>
          <span className="sidebar__icon">{n.icon}</span>
          {n.label}
        </NavLink>
      ))}

      <div className="sidebar__group">DIAGNOSTICS</div>
      {DIAGNOSTICS.map((n) => (
        <NavLink key={n.to} to={n.to} className={navClass}>
          <span className="sidebar__icon">{n.icon}</span>
          {n.label}
        </NavLink>
      ))}

      <div className="sidebar__group">SETTINGS</div>
      <NavLink to="/settings/data-sources" className={navClass}>
        <span className="sidebar__icon">⚙</span>
        Data sources
      </NavLink>
      <NavLink to="/about" className={navClass}>
        <span className="sidebar__icon">ⓘ</span>
        About
      </NavLink>

      {session && (
        <button
          type="button"
          onClick={signOut}
          style={{
            margin: "8px 14px 4px",
            padding: "8px 10px",
            background: "transparent",
            border: "1px solid var(--edge)",
            borderRadius: 7,
            color: "var(--ink-faint)",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            cursor: "pointer",
            textAlign: "left",
          }}
          title={`Signed in as ${session.email}`}
        >
          ⎋ Sign out
        </button>
      )}
      <div className="sidebar__foot">
        <div className="sidebar__foot-row">
          <span className="sidebar__foot-dot" />
          Coin economy · supply ⇄ demand
        </div>
        <div className="sidebar__vfd">
          Bvndle · powered by <b>VFD Group</b>
        </div>
      </div>
    </aside>
  );
}
